import { useRef, useState, useEffect, useCallback } from 'react';

export interface MediaPlayerState {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isFullscreen: boolean;
  playbackRate: number;
}

export interface MediaPlayerControls {
  ref: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  /** Ref for the container element that should go fullscreen */
  containerRef: React.RefObject<HTMLDivElement | null>;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (seconds: number) => void;
  setRate: (rate: number) => void;
  toggleFullscreen: () => void;
  /** Reload the media element (call when source changes) */
  load: () => void;
}

export type UseMediaPlayerReturn = MediaPlayerState & MediaPlayerControls;

export function useMediaPlayer(): UseMediaPlayerReturn {
  const ref = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onLoadedMetadata = () => setDuration(el.duration);
    const onDurationChange = () => setDuration(el.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onRateChange = () => setPlaybackRate(el.playbackRate);

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('ratechange', onRateChange);

    // Sync initial state if media is already loaded
    if (el.duration) setDuration(el.duration);
    if (el.currentTime) setCurrentTime(el.currentTime);
    if (!el.paused) setIsPlaying(true);

    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('ratechange', onRateChange);
    };
  }, []);

  // Track fullscreen changes (works for both Fullscreen API and iOS)
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const play = useCallback(() => ref.current?.play(), []);
  const pause = useCallback(() => ref.current?.pause(), []);
  const togglePlay = useCallback(() => {
    if (ref.current?.paused) ref.current.play();
    else ref.current?.pause();
  }, []);

  const load = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.load();
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, []);

  const seek = useCallback((seconds: number) => {
    if (ref.current) ref.current.currentTime = seconds;
  }, []);

  const setRate = useCallback((rate: number) => {
    if (ref.current) ref.current.playbackRate = rate;
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  }, []);

  return {
    ref,
    containerRef,
    currentTime,
    duration,
    isPlaying,
    isFullscreen,
    playbackRate,
    play,
    pause,
    togglePlay,
    seek,
    setRate,
    toggleFullscreen,
    load,
  };
}
